import React, { FC, ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { CSSTransition } from 'react-transition-group';
import { useButton } from '~Hooks';

/** In and out. The card inside uses the same number for its own transition. */
export const MODAL_TRANSITION_MS = 260;

/** Full-screen stage, portalled to body to escape any clipping ancestor. */
const Root = styled.div<{ zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${(props) => props.zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  box-sizing: border-box;
  font-family: var(--eposFontFamily);
`;

/**
 * Fades in as a plain keyframe the instant it mounts (a CSSTransition enter
 * class could race React's batching and skip to the end state) and fades
 * out through the exit classes, which carry the modal's `exitPrefix`.
 */
const Backdrop = styled.div<{ exitPrefix: string }>`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  animation: eposModalBackdropIn ${MODAL_TRANSITION_MS}ms ease-out both;
  transition: opacity ${MODAL_TRANSITION_MS}ms ease-out;

  .${(props) => props.exitPrefix}-exit-active &,
  .${(props) => props.exitPrefix}-exit-done & {
    animation: none;
    opacity: 0;
  }

  @keyframes eposModalBackdropIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

interface Props {
  open: boolean;
  /**
   * The CSSTransition class prefix, e.g. "epos-promo". The card inside
   * reads `.<exitPrefix>-exit-active &` to play its own way out.
   */
  exitPrefix: string;
  /** Tap on the dimmed area. Leave out for a notice that cannot be dismissed. */
  onBackdropTap?: () => void;
  /** Above other modals - only the notices need it. */
  zIndex?: number;
  testId: string;
  /** The card. */
  children: ReactNode;
}

/**
 * The shell every EPOS modal shares: portal, enter/exit transition, stage and
 * backdrop. The modal itself only renders its card as children and styles
 * its entrance and exit off `exitPrefix`.
 */
const Modal: FC<Props> = ({ open, exitPrefix, onBackdropTap, zIndex = 20, testId, children }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { touching, disabled, disabledToast, ...backdrop } = useButton({
    onClick: onBackdropTap ?? (() => undefined),
  });

  return createPortal(
    <CSSTransition
      in={open}
      nodeRef={rootRef}
      timeout={MODAL_TRANSITION_MS}
      classNames={exitPrefix}
      mountOnEnter
      unmountOnExit>
      <Root ref={rootRef} zIndex={zIndex} data-testid={testId}>
        <Backdrop exitPrefix={exitPrefix} {...(onBackdropTap ? backdrop : {})} />
        {children}
      </Root>
    </CSSTransition>,
    document.body,
  );
};

export default Modal;
